/**
 * Products Store (Pinia)
 *
 * @version 1.1.1
 * @description Manages product data from Metodo ERP.
 *              v1.1.0 (Fase 6): branching sorgente. Quando il settore
 *              selezionato è HORECA-FOOD (company MKT, codice settore 6) la
 *              sorgente NON è Metodo ma il middleware brand (useHorecaFoodStore,
 *              cross-brand). È il punto UNICO di branching per l'anagrafica:
 *              lista e dettaglio passano sempre da qui e non sanno la sorgente.
 *              v1.1.1: `extractSector` gestisce `searchBy` sia come oggetto sia
 *              come stringa JSON (com'è serializzato da useListPage), altrimenti
 *              il branch food non scattava mai dalla Lista prodotti.
 * @migrated-from store/products/products.ts (Vuex 4)
 */

import { defineStore, storeToRefs } from 'pinia'
import { ref } from 'vue'
import axios from 'axios'
import { standardizeErrorMessage } from '@/utils/utils'
import { useNavigationStore } from '@/stores/useNavigationStore'
import { useHorecaFoodStore } from '@/stores/useHorecaFoodStore'
import type { Pagination, ApiRequestOptions } from '@/stores/types'

// =============================================================================
// Store
// =============================================================================

export const useProductsStore = defineStore('products', () => {
  const { companyApiUrl, currentCompany } = storeToRefs(useNavigationStore())

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  const loading = ref(false)
  const products = ref<any[]>([])
  const productsPagination = ref<Pagination | null>(null)
  const currentOptions = ref<ApiRequestOptions>({})
  const productsOfCompanySector = ref('')

  // ---------------------------------------------------------------------------
  // Source detection (HORECA-FOOD)
  // ---------------------------------------------------------------------------

  /**
   * Il settore HORECA-FOOD ha codice 6 in company MKT. Nel selettore della
   * lista il valore settore è la chiave composta DITTA+CODICE (es. 'MKT6'),
   * stringa — diversa dal sector numerico usato nel dialog preventivi.
   */
  function isFoodSector(sectorValue: unknown): boolean {
    if (sectorValue == null) return false
    const company = (currentCompany.value ?? '').toUpperCase()
    return String(sectorValue).toUpperCase() === `${company}6`
  }

  /**
   * Estrae il valore del settore dalle opzioni, qualunque sia la forma con cui
   * arrivano. `useListPage`/`toLegacyApiParams` serializza `searchBy` in JSON
   * (stringa), mentre una chiamata diretta può passarlo come oggetto: vanno
   * gestiti entrambi, più il fallback `opts.sector` piatto.
   */
  function extractSector(opts: any): unknown {
    if (opts == null) return undefined

    const sb = opts.searchBy
    if (sb != null) {
      if (typeof sb === 'string') {
        try {
          const parsed = JSON.parse(sb)
          if (parsed != null && parsed.sector != null) return parsed.sector
        } catch {
          // searchBy non-JSON: ignora e prosegui coi fallback.
        }
      } else if (typeof sb === 'object' && sb.sector != null) {
        return sb.sector
      }
    }

    return opts.sector
  }

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  async function getProduct(productCode: string, opts?: { horecaFood?: boolean }): Promise<any> {
    // Branch sorgente: food -> middleware brand cross-brand, altrimenti Metodo.
    if (opts?.horecaFood === true) {
      return useHorecaFoodStore().getProduct(productCode)
    }

    loading.value = true
    try {
      const { data } = await axios.get(
        `${companyApiUrl.value}/metodo/product/${encodeURIComponent(productCode)}`
      )
      return data
    } catch (error) {
      throw standardizeErrorMessage(error)
    } finally {
      loading.value = false
    }
  }

  async function fetchProducts(options?: ApiRequestOptions): Promise<any> {
    if (options != null) currentOptions.value = options
    const opts = (options ?? currentOptions.value) as any

    // Branch sorgente: se il settore selezionato è HORECA-FOOD, delega al
    // middleware brand (cross-brand). La lista non sa la sorgente: riceve lo
    // stesso shape { data, pagination } e i prodotti già Metodo-compatibili.
    const sectorValue = extractSector(opts)
    if (isFoodSector(sectorValue)) {
      loading.value = true
      productsOfCompanySector.value = `${(currentCompany.value ?? '').toLowerCase()}6`
      try {
        const foodStore = useHorecaFoodStore()
        const data = await foodStore.fetchProducts(opts)
        products.value = data.data ?? []
        productsPagination.value = data.pagination ?? null
        return data
      } catch (error) {
        throw standardizeErrorMessage(error)
      } finally {
        loading.value = false
      }
    }

    loading.value = true

    productsOfCompanySector.value =
      `${currentCompany.value?.toLowerCase() ?? ''}${(options as any)?.sector ?? ''}`

    try {
      const { data } = await axios.get(`${companyApiUrl.value}/metodo/product`, {
        params: options ?? currentOptions.value,
      })
      products.value = data.data
      productsPagination.value = data.pagination ?? null
      return data
    } catch (error) {
      throw standardizeErrorMessage(error)
    } finally {
      loading.value = false
    }
  }

  async function patchProduct(product: any): Promise<any> {
    if (product.DITTA == null || product.CODICE == null) {
      throw { message: 'Product not applicable' }
    }

    loading.value = true
    try {
      const { data } = await axios.put(
        `${import.meta.env.VITE_API_URL}/${product.DITTA}/metodo/product/${encodeURIComponent(product.CODICE)}`,
        product
      )
      products.value = products.value.map((item: any) =>
        item.DITTA === data.DITTA && item.CODICE === data.CODICE ? data : item
      )
      return data
    } catch (error) {
      throw standardizeErrorMessage(error)
    } finally {
      loading.value = false
    }
  }

  async function updateImagesOrder(product: any, imagesOrder: any[]): Promise<any> {
    if (product.DITTA == null || product.CODICE == null) {
      throw { message: 'Product not applicable' }
    }

    loading.value = true
    try {
      const { data } = await axios.put(
        `${import.meta.env.VITE_API_URL}/${product.DITTA.toLowerCase()}/metodo/product/${encodeURIComponent(product.CODICE)}/reorderImages`,
        { imagesOrder }
      )
      _updateProductImages(data)
      return data
    } catch (error) {
      throw standardizeErrorMessage(error)
    } finally {
      loading.value = false
    }
  }

  async function deleteProductImage(product: any, imageId: number | string): Promise<any> {
    if (product.DITTA == null || product.CODICE == null) {
      throw { message: 'Product not applicable' }
    }

    loading.value = true
    try {
      const { data } = await axios.delete(
        `${import.meta.env.VITE_API_URL}/${product.DITTA.toLowerCase()}/metodo/product/${encodeURIComponent(product.CODICE)}/image/${imageId}`
      )
      _updateProductImages(data)
      return data
    } catch (error) {
      throw standardizeErrorMessage(error)
    } finally {
      loading.value = false
    }
  }

  async function storeProduct(product: any): Promise<any> {
    if (product == null) throw { message: 'Product not applicable' }

    loading.value = true
    try {
      const { data } = await axios.post(
        `${import.meta.env.VITE_API_URL}/metodo/product`,
        product
      )
      await fetchProducts()
      return data
    } catch (error) {
      throw standardizeErrorMessage(error)
    } finally {
      loading.value = false
    }
  }

  async function addProductImage(
    product: any,
    blob: Blob,
    filename: string,
    name: string,
    description: string
  ): Promise<any> {
    if (product.DITTA == null || product.CODICE == null) {
      throw { message: 'Product not applicable' }
    }

    loading.value = true
    try {
      const formData = new FormData()
      formData.append('image', blob, filename)
      formData.append('name', name)
      formData.append('description', description)

      const { data } = await axios.post(
        `${import.meta.env.VITE_API_URL}/${product.DITTA.toLowerCase()}/metodo/product/${encodeURIComponent(product.CODICE)}/image`,
        formData,
        { headers: { 'content-type': 'multipart/form-data' } }
      )
      _updateProductImages(data)
      return data
    } catch (error: any) {
      if (error?.data?.errors?.image && Array.isArray(error.data.errors.image)) {
        throw { message: error.data.errors.image[0] }
      }
      throw standardizeErrorMessage(error)
    } finally {
      loading.value = false
    }
  }

  // ---------------------------------------------------------------------------
  // Internal helpers
  // ---------------------------------------------------------------------------

  function _updateProductImages(product: any): void {
    const p = products.value.find(
      (item: any) => item.DITTA === product.DITTA && item.CODICE === product.CODICE
    )
    if (p != null) {
      p.images = product.images
    }
  }

  // ---------------------------------------------------------------------------
  // Return
  // ---------------------------------------------------------------------------

  return {
    loading,
    products,
    productsPagination,
    currentOptions,
    productsOfCompanySector,
    isFoodSector,
    getProduct,
    fetchProducts,
    patchProduct,
    updateImagesOrder,
    deleteProductImage,
    storeProduct,
    addProductImage,
  }
})
